import {AvatarUpload, Field, FieldInput, Icon, Modal, Typography} from '@sollapay/ui/components';
import {CopyButton} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {getInitials} from '@/utils';

import {useFirmSettingsForm} from '../hooks';

import type {FirmProfileDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface FirmSettingsModalProps {
  isOpen: boolean;
  profile?: FirmProfileDTO;
  onClose: () => void;
}

export const FirmSettingsModal: FC<FirmSettingsModalProps> = ({isOpen, profile, onClose}) => {
  const {t} = useTranslation(['settings', 'common']);
  const {
    form,
    handleSave,
    canEdit,
    isSaving,
    logoUrl,
    logoError,
    onLogoFileSelect,
    onRemoveLogo,
    canSave
  } = useFirmSettingsForm({isOpen, profile, onSuccess: onClose});
  const {
    register,
    formState: {errors}
  } = form;

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDirty={canSave}
      headerAlign="start"
      title={t('firmModal.title')}
      description={t('firmModal.description')}
      primaryLabel={canEdit ? t('firmModal.save') : undefined}
      onPrimaryAction={canEdit ? () => void handleSave() : undefined}
      primaryDisabled={!canSave}
      primaryLoading={isSaving}
      secondaryLabel={canEdit ? t('common:cancel') : t('firmModal.close')}
      onSecondaryAction={onClose}
    >
      <div className="flex flex-col gap-5">
        {!canEdit && (
          <div className="rounded-lg bg-background-secondary px-3 py-2">
            <Typography size="sm" color="tertiary">
              {t('firmModal.readOnlyBadge')}
            </Typography>
          </div>
        )}

        <AvatarUpload
          src={logoUrl}
          initials={getInitials(profile?.displayName)}
          shape="square"
          isUploading={isSaving}
          disabled={!canEdit}
          error={logoError}
          onFileSelect={onLogoFileSelect}
          onRemove={canEdit ? onRemoveLogo : undefined}
          titleLabel={t('firmModal.logo.title')}
          uploadLabel={t('firmModal.logo.upload')}
          dragLabel={t('firmModal.logo.drag')}
          removeLabel={t('firmModal.logo.remove')}
        />

        <FieldInput
          label={t('firmModal.fields.displayName.label')}
          description={t('firmModal.fields.displayName.description')}
          required
          disabled={!canEdit}
          error={errors.displayName?.message}
          {...register('displayName', {
            required: canEdit ? t('firmModal.fields.displayName.required') : false
          })}
        />

        <Field
          label={t('firmModal.fields.firmId.label')}
          description={t('firmModal.fields.firmId.description')}
        >
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <Icon name="lock" className="size-4 shrink-0 text-fg-tertiary" />
            <Typography size="sm" color="tertiary" className="flex-1 font-mono">
              {profile?.slug ?? ''}
            </Typography>
            {profile?.slug && <CopyButton text={profile.slug} />}
          </div>
        </Field>
      </div>
    </Modal>
  );
};
